import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaBan, FaCheck } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import axios from 'axios';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import LocationViewer from '../../../shared/components/LocationViewer';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminClientDetails = () => {
    const { setTitle } = useTitle();
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [banning, setBanning] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null });

    // دالة لتصحيح عرض رقم الهاتف
    const formatPhoneNumber = (phone) => {
        if (!phone) return 'غير محدد';
        
        // تحويل إلى نص للتأكد
        let phoneStr = String(phone).trim();
        
        // إزالة أي مسافات أو رموز غير مرغوب فيها
        phoneStr = phoneStr.replace(/\s+/g, '');
        
        // إذا كان الرقم يبدأ بـ + (مثل +201025376604) - نحوله إلى 201025376604+
        if (phoneStr.startsWith('+')) {
            return phoneStr.slice(1) + '+';
        }
        
        // إذا كان الرقم ينتهي بـ + (مثل 201025376604+) - نتركه كما هو
        if (phoneStr.endsWith('+')) {
            return phoneStr;
        }
        
        // إذا كان الرقم بدون + (مثل 201025376604) - نضيف + في النهاية
        return phoneStr + '+';
    };

    useEffect(() => {
        setTitle('تفاصيل المستخدم');
        fetchClientDetails();
    }, [setTitle, id]);

    const getAuthToken = () => {
        const adminData = sessionStorage.getItem('adminkhadamat');
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                // البحث عن التوكن في أماكن مختلفة
                if (parsed.data?.token?.access_token) {
                    return String(parsed.data.token.access_token);
                }
                if (parsed.data?.access_token) {
                    return String(parsed.data.access_token);
                }
                if (parsed.data?.token) {
                    return String(parsed.data.token);
                }
                if (parsed.token) {
                    return String(parsed.token);
                }
                return null;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchClientDetails = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/admin/clients/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            const apiStatus = response.data?.status;
            const responseStatus = response.status;
            
            if (apiStatus === 'success' || apiStatus === 200 || responseStatus === 200) {
                setClient(response.data.data);
            } else {
                throw new Error(response.data.message || 'فشل في جلب تفاصيل المستخدم');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في جلب تفاصيل المستخدم');
            navigate('/admin-clients');
        } finally {
            setLoading(false);
        }
    };

    const handleBanUser = async (banStatus) => {
        setConfirmModal({
            isOpen: true,
            data: banStatus,
            message: banStatus === 1 ? 'هل أنت متأكد من حظر هذا المستخدم؟' : 'هل أنت متأكد من إلغاء حظر هذا المستخدم؟',
            title: banStatus === 1 ? 'تأكيد الحظر' : 'تأكيد إلغاء الحظر',
            confirmButtonColor: banStatus === 1 ? '#dc3545' : '#28a745'
        });
    };

    const executeBanUser = async (banStatus) => {
        try {
            setBanning(true);
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/admin/clients/${id}/ban?ban=${banStatus}`, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            const apiStatus = response.data?.status;
            const responseStatus = response.status;
            
            if (apiStatus === 'success' || apiStatus === 200 || responseStatus === 200) {
                showSuccessToast(banStatus === 1 ? 'تم حظر المستخدم بنجاح' : 'تم إلغاء حظر المستخدم بنجاح');
                fetchClientDetails(); // Refresh data
            } else {
                throw new Error(response.data.message || 'فشل في العملية');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في العملية');
        } finally {
            setBanning(false);
        }
    };

    const getStatusBadge = (banStatus) => {
        if (banStatus === "1" || banStatus === 1) {
            return <span className="badge bg-danger fs-6 text-white">محظور</span>;
        }
        return <span className="badge bg-success fs-6 text-white">غير محظور</span>;
    };

    // تم إلغاء عرض حالة النشاط

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل المستخدم...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!client) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <FaUser className="text-muted" style={{ fontSize: '3rem' }} />
                        <p className="mt-3 text-muted">المستخدم غير موجود</p>
                        <button className="btn btn-primary" onClick={() => navigate('/admin-clients')}>
                            العودة للمستخدمين
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/admin-clients')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة للمستخدمين
                    </motion.button>
                    <div className="d-flex gap-2">
                        {client.ban === "1" || client.ban === 1 ? (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn text-white d-flex align-items-center"
                                onClick={() => handleBanUser(0)}
                                disabled={banning}
                                style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                                <FaCheck className="mx-2" />
                                {banning ? 'جاري...' : 'إلغاء الحظر'}
                            </motion.button>
                        ) : (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-danger d-flex align-items-center"
                                onClick={() => handleBanUser(1)}
                                disabled={banning}>
                                <FaBan className="mx-2" />
                                {banning ? 'جاري...' : 'حظر المستخدم'}
                            </motion.button>
                        )}
                    </div>
                </div>

                {/* Client Details */}
                <div className="row">
                    {/* Basic Info */}
                    <div className="col-md-8">
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0">المعلومات الأساسية</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">الاسم</label>
                                        <div className="d-flex align-items-center">
                                            <FaUser className="text-muted mx-2" />
                                            <span>{client.name || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">البريد الإلكتروني</label>
                                        <div className="d-flex align-items-center">
                                            <FaEnvelope className="text-muted mx-2" />
                                            <span>{client.email || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">رقم الهاتف</label>
                                        <div className="d-flex align-items-center">
                                            <FaPhone className="text-muted mx-2" />
                                            <span>{formatPhoneNumber(client.phone)}</span>
                                        </div>
                                    </div>
                                    {/* تم إلغاء عرض حالة النشاط */}
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">حالة الحظر</label>
                                        <div className="d-flex align-items-center">
                                            {getStatusBadge(client.ban)}
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <label className="form-label fw-semibold">العنوان</label>
                                        <div className="d-flex align-items-start">
                                            <FaMapMarkerAlt className="text-muted mx-2 mt-1" />
                                            <span className="address-text">{client.default_address?.area || client.address || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    
                                    {/* العنوان الافتراضي */}
                                    {client.default_address && (
                                        <>
                                            <div className="col-md-12 mb-3">
                                                <label className="form-label fw-semibold">العنوان الافتراضي</label>
                                                <div className="card bg-light">
                                                    <div className="card-body p-3">
                                                        <div className="row">
                                                            <div className="col-md-6 mb-2">
                                                                <small className="text-muted">العنوان:</small>
                                                                <p className="mb-0 fw-medium">{client.default_address.title || 'غير محدد'}</p>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                                <small className="text-muted">رقم المبنى:</small>
                                                                <p className="mb-0 fw-medium">{client.default_address.build_num || 'غير محدد'}</p>
                                                            </div>
                                                            <div className="col-md-12 mb-2">
                                                                <small className="text-muted">المنطقة:</small>
                                                                <p className="mb-0 fw-medium">{client.default_address.area || 'غير محدد'}</p>
                                                            </div>
                                                            {client.default_address.description && (
                                                                <div className="col-md-12 mb-2">
                                                                    <small className="text-muted">الوصف:</small>
                                                                    <p className="mb-0 fw-medium">{client.default_address.description}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* عرض الموقع على الخريطة */}
                        {client.default_address && client.default_address.lat && client.default_address.lng && (
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="mb-0">
                                        <FaMapMarkerAlt className="mx-2" />
                                        الموقع على الخريطة
                                    </h5>
                                </div>
                                <div className="card-body p-0">
                                    <LocationViewer
                                        lat={client.default_address.lat}
                                        lng={client.default_address.lng}
                                        height="400px"
                                        title={client.default_address.title || 'موقع العميل'}
                                    />
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Status Card */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">حالة الحساب</h5>
                            </div>
                            <div className="card-body text-center">
                                <div className="mb-3">
                                    <div className="mx-auto mb-3" style={{ width: '80px', height: '80px' }}>
                                        {client.prof_img ? (
                                            <img
                                                src={client.prof_img}
                                                alt={client.name || 'مستخدم'}
                                                className="w-100 h-100"
                                                style={{ objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div className="bg-primary text-white d-flex align-items-center justify-content-center w-100 h-100">
                                                <FaUser style={{ fontSize: '2rem' }} />
                                            </div>
                                        )}
                                    </div>
                                    <h6>{client.name || 'غير محدد'}</h6>
                                    <p className="text-muted">{client.email || 'غير محدد'}</p>
                                </div>
                                
                             

                                <div className="d-grid gap-2">
                                    {client.ban === "1" || client.ban === 1 ? (
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="btn text-white"
                                            onClick={() => handleBanUser(0)}
                                            disabled={banning}
                                            style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                                            <FaCheck className="mx-2" />
                                            {banning ? 'جاري...' : 'إلغاء الحظر'}
                                        </motion.button>
                                    ) : (
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="btn btn-danger"
                                            onClick={() => handleBanUser(1)}
                                            disabled={banning}>
                                            <FaBan className="mx-2" />
                                            {banning ? 'جاري...' : 'حظر المستخدم'}
                                        </motion.button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, data: null })}
                onConfirm={() => executeBanUser(confirmModal.data)}
                title={confirmModal.title || 'تأكيد العملية'}
                message={confirmModal.message || 'هل أنت متأكد من هذا الإجراء؟'}
                confirmText="تأكيد"
                cancelText="إلغاء"
                confirmButtonColor={confirmModal.confirmButtonColor || '#dc3545'}
                type={confirmModal.data === 1 ? 'danger' : 'success'}
            />
        </div>
    );
};

export default AdminClientDetails;

// CSS للعناوين
const style = document.createElement('style');
style.textContent = `
  .address-text {
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    line-height: 1.5;
    max-width: 100%;
  }
`;
document.head.appendChild(style);
